import React from "react";

import type { Time } from "~/tools/date-time/time";

import { SpecificRange, type TimeRange } from "../../app/state/view/timeline/TimeRange";
import type { TimeSeriesChartState } from "../TimeSeriesChart/state";
import type { TimeSeries } from "../TimeSeriesChart/time-series";

import { PaginatedStatChartWidget } from "./stat-chart/paginated";
import type { Spec } from "./spec";

const DEFAULT_PAGE_SIZE = 20;

const LazyTimeSeriesChartWidget = React.lazy(async () => {
	const { PaginatedTimeSeriesChartWidget } = await import("./time-series-chart/paginated");
	return { default: PaginatedTimeSeriesChartWidget };
});

const LazyBarChartWidget = React.lazy(async () => {
	const { PaginatedBarChartWidget } = await import("./bar-chart/paginated");
	return { default: PaginatedBarChartWidget };
});

const LazyGaugeChartWidget = React.lazy(async () => {
	const { PaginatedGaugeChartWidget } = await import("./gauge-chart/paginated");
	return { default: PaginatedGaugeChartWidget };
});

export interface PaginatedChartProps {
	pageSize?: number;
	spec: Spec;
	state?: TimeSeriesChartState;
	timeSeries: ReadonlyArray<TimeSeries>;
	timeRange: TimeRange;
	onTimeRangeChange?: (timeRange: SpecificRange) => void;
	/**
	 * The React.Suspense's fallback
	 */
	fallback: React.ReactNode;
	/**
	 * Only applied when `spec.kind` is `TimeSeriesChart` or `BarChart`.
	 * Ignored for `GaugeChart` and `StatChart`.
	 */
	legendMaxLines?: number;
	/**
	 * Only applied when `spec.kind` is `TimeSeriesChart` or `BarChart`.
	 * Ignored for `GaugeChart` and `StatChart`.
	 */
	markerTimestamp?: Time;
}

export const PaginatedChart: React.FC<PaginatedChartProps> = ({
	legendMaxLines,
	markerTimestamp,
	pageSize = DEFAULT_PAGE_SIZE,
	spec,
	state,
	timeSeries,
	timeRange,
	onTimeRangeChange,
	fallback,
}) => (
	<React.Suspense fallback={fallback}>
		{spec.kind === "TimeSeriesChart" && (
			<LazyTimeSeriesChartWidget
				className="h-full"
				legendMaxLines={legendMaxLines}
				markerTimestamp={markerTimestamp}
				onBrush={
					onTimeRangeChange == null ? undefined : (start, end) => onTimeRangeChange(new SpecificRange(start, end))
				}
				pageSize={pageSize}
				state={state}
				timeRange={timeRange}
				timeSeries={timeSeries}
				{...spec.settings}
			/>
		)}

		{spec.kind === "BarChart" && (
			<LazyBarChartWidget
				className="h-full"
				legendMaxLines={legendMaxLines}
				markerTimestamp={markerTimestamp}
				onBrush={
					onTimeRangeChange == null ? undefined : (start, end) => onTimeRangeChange(new SpecificRange(start, end))
				}
				pageSize={pageSize}
				state={state}
				timeRange={timeRange}
				timeSeries={timeSeries}
				{...spec.settings}
			/>
		)}

		{spec.kind === "GaugeChart" && (
			<LazyGaugeChartWidget
				className="h-full"
				minCellSize={120}
				timeRange={timeRange}
				timeSeries={timeSeries}
				{...spec.settings}
			/>
		)}

		{spec.kind === "StatChart" && (
			<PaginatedStatChartWidget
				className="h-full"
				minCellSize={120}
				timeRange={timeRange}
				timeSeries={timeSeries}
				{...spec.settings}
			/>
		)}
	</React.Suspense>
);
